import React, { useState } from "react";
import {
  Paper,
  InputBase,
  IconButton,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";

/**
 * A responsive search input component with optional filter button
 *
 * @param {Object} props
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.onSearch - Callback function when search is submitted
 * @param {Function} props.onFilterClick - Optional callback for filter button click
 * @param {boolean} props.showFilter - Whether to show the filter button
 * @param {Object} props.sx - Additional styles to apply
 */
const SearchInput = ({
  placeholder = "Search...",
  onSearch,
  onFilterClick,
  showFilter = false,
  sx = {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim() && onSearch) {
      onSearch(searchTerm);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    if (onSearch) {
      onSearch("");
    }
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: isMobile ? "2px" : "2px 4px",
          display: "flex",
          alignItems: "center",
          width: "100%",
          borderRadius: isMobile ? 1.5 : 2,
        }}
        elevation={1}
      >
        <IconButton
          type="submit"
          sx={{ p: isMobile ? "8px" : "10px" }}
          aria-label="search"
          size={isMobile ? "small" : "medium"}
        >
          <SearchIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>

        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder={placeholder}
          inputProps={{
            "aria-label": placeholder,
            style: { fontSize: isMobile ? "0.875rem" : "1rem" },
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
        />

        {searchTerm && (
          <IconButton
            sx={{ p: isMobile ? "8px" : "10px" }}
            aria-label="clear search"
            onClick={handleClear}
            size={isMobile ? "small" : "medium"}
          >
            <ClearIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        )}

        {showFilter && (
          <>
            <Divider
              sx={{ height: isMobile ? 24 : 28, m: 0.5 }}
              orientation="vertical"
            />
            <IconButton
              color="primary"
              sx={{ p: isMobile ? "8px" : "10px" }}
              aria-label="filter"
              onClick={onFilterClick}
              size={isMobile ? "small" : "medium"}
            >
              <FilterIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SearchInput;
